import React from 'react'
import { Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import type { FieldConfig } from '../types.ts'

export interface FieldLabelContentProps {
  field: FieldConfig
  showColon?: boolean
}

/**
 * The inside of every InputManager field label: the required marker, the label
 * text, the optional colon, and — when the field declares `labelTooltip` — a
 * help "?" carrying the explanation.
 *
 * One component rather than five copies of the same JSX: InputManager renders
 * labels in five places (above / inline / wrapped, plus the two halves of a
 * `groupWithNext` pair), and before this the markup was duplicated in each, so
 * any addition had to be made five times or it silently applied to some layouts
 * only.
 *
 * The "?" is for a vocabulary the user is not expected to know — Critical
 * Technology Area is the first — not for restating the field name. It stops
 * click propagation so clicking it cannot also open the field for editing.
 */
export function FieldLabelContent({ field, showColon }: FieldLabelContentProps) {
  return (
    <>
      {field.required && <span className='text-red-500 mr-1'>*</span>}
      {field.label}
      {showColon ? ':' : ''}
      {field.labelTooltip ? (
        <Tooltip title={field.labelTooltip} overlayStyle={{ maxWidth: 460 }} placement='topLeft'>
          <QuestionCircleOutlined
            className='ml-1 cursor-help align-middle'
            style={{ color: '#4C90E9' }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          />
        </Tooltip>
      ) : null}
    </>
  )
}
